// /admin/recipes/:id: one recipe. When it was reported, the reports come first with
// "Keep the recipe" (clears the reports) and "Remove the recipe" (deletes it).
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import {
  deleteRecipe,
  exportRecipes,
  getRecipe,
  getRecipeReports,
  keepRecipe,
} from '../../../api/adminV2'
import { CATEGORIES_RECIPE, DATA_DROP_REPORT, OPTIONS_TBLS } from '../../../utils/constants'
import { useCounts } from '../AdminLayout'
import {
  copyText,
  formatDate,
  LoadError,
  Loading,
  Pill,
  recipeStatusKey,
  saveFile,
  useFeedback,
  useLoad,
} from '../ui'

const capitalise = (s = '') => (s ? s[0].toUpperCase() + s.slice(1) : s)

// Reports from the new endpoint, or (if the server is older) from the recipe itself
const loadRecipe = (id) =>
  Promise.all([
    getRecipe(id),
    getRecipeReports(id).catch((e) => {
      if (e?.response?.status === 404) return null
      throw e
    }),
  ]).then(([recipe, reports]) => ({
    recipe,
    reports:
      reports ||
      (recipe?.reports || []).map((r) => ({
        user_id: r.user_id,
        reporter_name: r.author?.fullname,
        reporter_email: r.author?.email,
        type: r.type,
        type_label: capitalise(DATA_DROP_REPORT.find((d) => d.id === r.type)?.name || 'Other'),
        message: r.message,
      })),
  }))

const unit = (m) => {
  if (m === null || m === undefined || m === '') return ''
  const known = OPTIONS_TBLS.find((o) => o.id === Number(m))
  return known && /^\d+$/.test(String(m)) ? known.name : String(m)
}

const Recipe = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { toast, toastError, confirm } = useFeedback()
  const { refreshCounts } = useCounts()
  const { data, error, loading, reload } = useLoad(() => loadRecipe(id), [id])
  const [busy, setBusy] = useState('')
  const [showEmail, setShowEmail] = useState(false)
  const [photoBroken, setPhotoBroken] = useState(false)

  if (error && !data) return <LoadError error={error} onRetry={reload} />
  if (loading && !data) return <Loading />
  if (!data?.recipe) return null

  const r = data.recipe
  const reports = data.reports || []
  const statusKey = recipeStatusKey(r.status)
  const flagged = statusKey === 'flagged' || reports.length > 0
  const author = r.author || {}
  const groupNames = (author.groups || [])
    .filter((g) => (r.groups || []).map(Number).includes(Number(g.id)))
    .map((g) => g.name)
  const categories = CATEGORIES_RECIPE.filter((c) =>
    (r.categories || []).map(Number).includes(c.id)
  )
  const ingredients = (r.ingredients || []).filter((i) => i.name || i.quantity)
  const steps = [...(r.steps || [])]
    .filter((s) => s.description)
    .sort((a, b) => Number(a.step || 0) - Number(b.step || 0))
  const howAdded = r.source_url ? 'from a link' : ''

  const keep = () =>
    confirm({
      title: 'Keep this recipe?',
      text: 'The reports are cleared and the recipe is visible to its groups again.',
      action: 'Keep the recipe',
      run: () => keepRecipe(r.id),
    }).then((ok) => {
      if (!ok) return
      toast('Recipe is visible again and the report is cleared')
      refreshCounts()
      reload()
    })

  const remove = () =>
    confirm({
      title: 'Remove this recipe?',
      text: "It's deleted from Morsel for everyone. This can't be undone.",
      action: 'Remove recipe',
      danger: true,
      run: () => deleteRecipe(r.id),
    }).then((ok) => {
      if (!ok) return
      toast(`"${r.name || 'The recipe'}" was removed`)
      refreshCounts()
      navigate('/admin/recipes')
    })

  const download = () => {
    setBusy('download')
    exportRecipes({ ids: [r.id] })
      .then((bytes) => {
        saveFile(bytes, `${r.name || 'Recipe'}.xlsx`)
        toast('Spreadsheet downloaded')
      })
      .catch((e) => toastError(e, 'The spreadsheet could not be made.'))
      .finally(() => setBusy(''))
  }

  const copyEmail = () =>
    copyText(author.email)
      .then(() => toast('Email address copied'))
      .catch(() => toastError('Could not copy. Select the address and copy it yourself.'))

  return (
    <>
      <div>
        <Link className="a-crumb" to="/admin/recipes">
          ← Recipes
        </Link>
        <div className="a-head">
          <div style={{ minWidth: 0 }}>
            <h1>{r.name || 'Untitled recipe'}</h1>
            <p className="a-sub">
              by{' '}
              {r.user_id ? (
                <Link to={`/admin/people/${r.user_id}`}>{author.fullname || 'unknown'}</Link>
              ) : (
                author.fullname || 'unknown'
              )}
              {groupNames.length ? ` · ${groupNames.join(', ')}` : ''} · added{' '}
              {formatDate(r.created_at)}
              {howAdded ? ` · ${howAdded}` : ''}
            </p>
          </div>
          <div className="a-btn-row">
            <Pill status={statusKey} />
            <Link className="a-btn" to={`/admin/recipes/${r.id}/edit`}>
              Edit recipe
            </Link>
            <button
              type="button"
              className="a-btn"
              onClick={download}
              disabled={busy === 'download'}
              aria-disabled={busy === 'download'}
            >
              {busy === 'download' ? 'Preparing…' : 'Download'}
            </button>
          </div>
        </div>
      </div>

      {flagged ? (
        <section className="a-panel">
          <div className="a-panel-head">
            <h2>
              Reported by {reports.length} {reports.length === 1 ? 'person' : 'people'}
            </h2>
            <span className="a-muted">Hidden from the group until you decide</span>
          </div>
          {reports.map((rep, i) => (
            <div className="a-report" key={`${rep.user_id}-${i}`}>
              <b>{rep.type_label || 'Other'}</b>
              {rep.message ? <span>"{rep.message}"</span> : null}
              <span className="a-muted a-small-text">
                {rep.user_id ? (
                  <Link to={`/admin/people/${rep.user_id}`}>
                    {rep.reporter_name || 'Unknown person'}
                  </Link>
                ) : (
                  rep.reporter_name || 'Unknown person'
                )}
                {rep.reporter_email ? ` · ${rep.reporter_email}` : ''}
              </span>
            </div>
          ))}
          <div className="a-btn-row">
            <button type="button" className="a-btn a-primary" onClick={keep}>
              Keep the recipe
            </button>
            <button type="button" className="a-btn a-danger" onClick={remove}>
              Remove the recipe
            </button>
            {author.email ? (
              <button type="button" className="a-btn" onClick={() => setShowEmail((v) => !v)}>
                Contact the author
              </button>
            ) : null}
          </div>
          {showEmail && author.email ? (
            <div className="a-btn-row">
              <span>
                {author.fullname ? `${author.fullname}: ` : ''}
                <a href={`mailto:${author.email}`}>{author.email}</a>
              </span>
              <button type="button" className="a-btn a-small" onClick={copyEmail}>
                Copy email
              </button>
            </div>
          ) : null}
        </section>
      ) : null}

      <section className="a-panel">
        <div className="a-recipe-hero">
          <div className="a-photo">
            {r.image && !photoBroken ? (
              <img
                src={r.image}
                alt={`Photo of ${r.name || 'the recipe'}`}
                onError={() => setPhotoBroken(true)}
              />
            ) : (
              'no photo'
            )}
          </div>
          <div className="a-stack">
            {r.description ? <p className="a-muted">{r.description}</p> : null}
            {categories.length ? (
              <div className="a-btn-row">
                {categories.map((c) => (
                  <span className="a-tag" key={c.id}>
                    {c.name}
                  </span>
                ))}
              </div>
            ) : null}
            <div className="a-grid2">
              <div>
                <h2>Ingredients</h2>
                {r.amount ? (
                  <p className="a-muted a-small-text" style={{ marginTop: 4 }}>
                    Makes {r.amount} {r.measurement || ''}
                  </p>
                ) : null}
                {ingredients.length ? (
                  <ul className="a-lines a-muted">
                    {ingredients.map((ing, i) => (
                      <li key={ing.id || i}>
                        {[ing.quantity, unit(ing.measurement), ing.name].filter(Boolean).join(' ')}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="a-muted" style={{ marginTop: 6 }}>
                    None yet
                  </p>
                )}
              </div>
              <div>
                <h2>Steps</h2>
                {steps.length ? (
                  <ol className="a-steps a-muted">
                    {steps.map((s, i) => (
                      <li key={s.id || i}>{s.description}</li>
                    ))}
                  </ol>
                ) : (
                  <p className="a-muted" style={{ marginTop: 6 }}>
                    None yet
                  </p>
                )}
              </div>
            </div>
            {r.source_url ? (
              <p className="a-small-text a-muted">
                Source:{' '}
                <a href={r.source_url} target="_blank" rel="noopener noreferrer">
                  {r.source_name || r.source_url}
                </a>
              </p>
            ) : null}
          </div>
        </div>
      </section>

      {!flagged ? (
        <section className="a-panel a-danger-zone">
          <h2>Delete this recipe</h2>
          <p className="a-muted">
            Deletes it from Morsel for everyone, including every group it is in.
          </p>
          <div>
            <button type="button" className="a-btn a-danger" onClick={remove}>
              Delete recipe
            </button>
          </div>
        </section>
      ) : null}
    </>
  )
}

export default Recipe
